import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import Autocomplete from '../components/SearchableAutocomplete';
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { useTranslation } from "react-i18next";
import SidebarLayout from "../components/sidebarLayout";
import { getAllProdotti } from "../APIs/ProductsAPI";
import {
  createFixedPriceMenu,
  deactivateFixedPriceMenu,
  deleteFixedPriceMenu,
  getAllFixedPriceMenus,
  updateFixedPriceMenu,
} from "../APIs/FixedPriceMenusAPI";
import { useSnackbar } from "../contexts/SnackBarContext";

const localDate = (value = new Date()) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const newGroup = (courseIndex, t) => ({
  clientId: `group-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  name: courseIndex === 1
    ? t("fixedPriceMenu.defaultFirstCourse")
    : courseIndex === 2
      ? t("fixedPriceMenu.defaultSecondCourse")
      : courseIndex === 3
        ? t("fixedPriceMenu.defaultDessertCourse")
        : t("fixedPriceMenu.defaultCourse", { index: courseIndex }),
  courseIndex,
  minSelections: 1,
  maxSelections: 1,
  options: [],
});

const emptyForm = (t) => ({
  id: null,
  name: t("fixedPriceMenu.defaultMenuName"),
  date: localDate(),
  basePrice: "",
  active: true,
  groups: [newGroup(1, t), newGroup(2, t), newGroup(3, t)],
});

const normalizeMenu = (menu) => ({
  id: menu.id,
  name: menu.name || "",
  date: localDate(menu.date),
  basePrice: Number(menu.basePrice || 0),
  active: Boolean(menu.active),
  groups: (menu.groups || []).map((group) => ({
    clientId: `group-${group.id}`,
    id: group.id,
    name: group.name || "",
    courseIndex: Number(group.courseIndex) || 1,
    minSelections: Number(group.minSelections) || 0,
    maxSelections: Number(group.maxSelections) || 1,
    options: (group.options || []).map((option) => ({
      productId: Number(option.productId),
      extraPrice: Number(option.extraPrice || 0),
    })),
  })),
});

const toPayload = (form) => ({
  name: form.name.trim(),
  date: `${form.date}T12:00:00.000Z`,
  basePrice: Number(form.basePrice),
  active: Boolean(form.active),
  groups: [...form.groups]
    .sort((a, b) => Number(a.courseIndex) - Number(b.courseIndex))
    .map((group) => ({
      name: group.name.trim(),
      courseIndex: Number(group.courseIndex),
      minSelections: Number(group.minSelections),
      maxSelections: Number(group.maxSelections),
      options: group.options.map((option) => ({
        productId: Number(option.productId),
        extraPrice: Number(option.extraPrice || 0),
      })),
    })),
});

function FixedPriceMenusPage() {
  const { t } = useTranslation();
  const tenantType = String(localStorage.getItem("tenantType") || "").toUpperCase();
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const { showSnackbar } = useSnackbar();
  const [menus, setMenus] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(() => emptyForm(t));
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (tenantType !== "RISTORANTE") return;
    setLoading(true);
    try {
      const [menuData, productData] = await Promise.all([
        getAllFixedPriceMenus(token, tenant),
        getAllProdotti(token, tenant),
      ]);
      setMenus(Array.isArray(menuData) ? menuData : []);
      setProducts(Array.isArray(productData) ? productData : []);
    } catch (error) {
      showSnackbar(error.message || t("fixedPriceMenu.loadError"), "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, t, tenant, tenantType, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const productById = useMemo(
    () => new Map(products.map((product) => [Number(product.id), product])),
    [products]
  );

  const updateGroup = (clientId, patch) => {
    setForm((current) => ({
      ...current,
      groups: current.groups.map((group) => (
        group.clientId === clientId ? { ...group, ...patch } : group
      )),
    }));
  };

  const selectedProductsForGroup = (group) => (
    group.options
      .map((option) => productById.get(Number(option.productId)))
      .filter(Boolean)
  );

  const setGroupProducts = (group, selectedProducts) => {
    const currentExtras = new Map(
      group.options.map((option) => [Number(option.productId), Number(option.extraPrice || 0)])
    );
    updateGroup(group.clientId, {
      options: selectedProducts.map((product) => ({
        productId: Number(product.id),
        extraPrice: currentExtras.get(Number(product.id)) || 0,
      })),
    });
  };

  const validate = () => {
    if (!form.name.trim() || form.basePrice === "" || Number(form.basePrice) < 0) {
      return t("fixedPriceMenu.validationMainFields");
    }
    if (!form.groups.length) return t("fixedPriceMenu.validationAddCourse");
    const courseIndexes = form.groups.map((group) => Number(group.courseIndex));
    if (courseIndexes.some((courseIndex) => !Number.isInteger(courseIndex) || courseIndex <= 0)) {
      return t("fixedPriceMenu.validationCourseOrderPositive");
    }
    if (new Set(courseIndexes).size !== courseIndexes.length) {
      return t("fixedPriceMenu.validationCourseOrderUnique");
    }
    for (const group of form.groups) {
      if (!group.name.trim() || !group.options.length) return t("fixedPriceMenu.validationCourseContent");
      if (Number(group.minSelections) < 0 || Number(group.maxSelections) < 1) return t("fixedPriceMenu.validationSelectionLimits");
      if (Number(group.minSelections) > Number(group.maxSelections)) return t("fixedPriceMenu.validationMinimumMaximum");
      if (Number(group.maxSelections) > group.options.length) return t("fixedPriceMenu.validationMaximumOptions");
    }
    return "";
  };

  const save = async () => {
    const validationError = validate();
    if (validationError) {
      showSnackbar(validationError, "warning");
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (form.id) await updateFixedPriceMenu(form.id, payload, token, tenant);
      else await createFixedPriceMenu(payload, token, tenant);
      showSnackbar(t("fixedPriceMenu.saved"), "success");
      setOpen(false);
      await loadData();
    } catch (error) {
      showSnackbar(error.message || t("fixedPriceMenu.saveError"), "error");
    } finally {
      setSaving(false);
    }
  };

  if (tenantType !== "RISTORANTE") {
    return (
      <SidebarLayout className="admin-responsive-page">
        <Container sx={{ py: 4 }}>
          <Alert severity="warning">{t("fixedPriceMenu.restaurantOnly")}</Alert>
        </Container>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout className="admin-responsive-page">
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2} mb={3}>
          <Box>
            <Typography variant="h4" fontWeight={900}>{t("fixedPriceMenu.title")}</Typography>
            <Typography color="text.secondary">
              {t("fixedPriceMenu.subtitle")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setForm(emptyForm(t));
              setOpen(true);
            }}
          >
            {t("fixedPriceMenu.newMenu")}
          </Button>
        </Stack>

        {loading ? (
          <Typography>{t("fixedPriceMenu.loading")}</Typography>
        ) : menus.length === 0 ? (
          <Alert severity="info">{t("fixedPriceMenu.noMenus")}</Alert>
        ) : (
          <Grid container spacing={2}>
            {menus.map((menu) => (
              <Grid item xs={12} md={6} lg={4} key={menu.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" gap={1}>
                      <Box>
                        <Typography variant="h6" fontWeight={900}>{menu.name}</Typography>
                      </Box>
                      <Chip
                        label={menu.active ? t("fixedPriceMenu.active") : t("fixedPriceMenu.inactive")}
                        color={menu.active ? "success" : "default"}
                      />
                    </Stack>
                    <Typography variant="h5" color="primary" fontWeight={900} mt={2}>
                      EUR {Number(menu.basePrice || 0).toFixed(2)}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Stack spacing={0.5}>
                      {(menu.groups || []).map((group) => (
                        <Typography key={group.id} variant="body2">
                          {group.courseIndex}. {group.name}: {t("fixedPriceMenu.choicesAndDishes", {
                            min: group.minSelections,
                            max: group.maxSelections,
                            count: group.options?.length || 0,
                          })}
                        </Typography>
                      ))}
                    </Stack>
                  </CardContent>
                  <CardActions>
                    <Button
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => {
                        setForm(normalizeMenu(menu));
                        setOpen(true);
                      }}
                    >
                      {t("fixedPriceMenu.edit")}
                    </Button>
                    {menu.active && (
                      <Button
                        color="warning"
                        onClick={async () => {
                          try {
                            await deactivateFixedPriceMenu(menu.id, token, tenant);
                            showSnackbar(t("fixedPriceMenu.deactivated"), "success");
                            await loadData();
                          } catch (error) {
                            showSnackbar(error.message || t("fixedPriceMenu.deactivateError"), "error");
                          }
                        }}
                      >
                        {t("fixedPriceMenu.deactivate")}
                      </Button>
                    )}
                    <IconButton
                      color="error"
                      onClick={async () => {
                        if (!window.confirm(t("fixedPriceMenu.deleteConfirm", { name: menu.name }))) return;
                        try {
                          await deleteFixedPriceMenu(menu.id, token, tenant);
                          showSnackbar(t("fixedPriceMenu.deleted"), "success");
                          await loadData();
                        } catch (error) {
                          showSnackbar(error.message || t("fixedPriceMenu.deleteError"), "error");
                        }
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" gap={1}>
            <RestaurantMenuIcon color="primary" />
            <Typography variant="h6" fontWeight={900}>
              {form.id ? t("fixedPriceMenu.editMenu") : t("fixedPriceMenu.newMenu")}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label={t("fixedPriceMenu.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="number" label={t("fixedPriceMenu.basePrice")} inputProps={{ min: 0, step: 0.01 }} value={form.basePrice} onChange={(e) => setForm({ ...form, basePrice: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />} label={t("fixedPriceMenu.active")} />
            </Grid>
          </Grid>

          <Stack spacing={2} mt={3}>
            {form.groups.map((group) => (
              <Card variant="outlined" key={group.clientId}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <TextField fullWidth label={t("fixedPriceMenu.courseName")} value={group.name} onChange={(e) => updateGroup(group.clientId, { name: e.target.value })} />
                    </Grid>
                    <Grid item xs={4} md={2}>
                      <TextField fullWidth type="number" label={t("fixedPriceMenu.courseOrder")} inputProps={{ min: 1, step: 1 }} value={group.courseIndex} onChange={(e) => updateGroup(group.clientId, { courseIndex: e.target.value })} />
                    </Grid>
                    <Grid item xs={4} md={2}>
                      <TextField fullWidth type="number" label={t("fixedPriceMenu.minimum")} inputProps={{ min: 0, step: 1 }} value={group.minSelections} onChange={(e) => updateGroup(group.clientId, { minSelections: e.target.value })} />
                    </Grid>
                    <Grid item xs={4} md={2}>
                      <TextField fullWidth type="number" label={t("fixedPriceMenu.maximum")} inputProps={{ min: 1, step: 1 }} value={group.maxSelections} onChange={(e) => updateGroup(group.clientId, { maxSelections: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <IconButton
                        color="error"
                        onClick={() => setForm((current) => ({
                          ...current,
                          groups: current.groups.filter((item) => item.clientId !== group.clientId),
                        }))}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        options={products}
                        value={selectedProductsForGroup(group)}
                        getOptionLabel={(option) => option.name || t("fixedPriceMenu.productFallback", { id: option.id })}
                        isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                        onChange={(_, value) => setGroupProducts(group, value)}
                        renderInput={(params) => <TextField {...params} label={t("fixedPriceMenu.availableDishes")} />}
                      />
                    </Grid>
                  </Grid>

                  {group.options.length > 0 && (
                    <Stack spacing={1} mt={2}>
                      <Typography variant="subtitle2">{t("fixedPriceMenu.optionalSupplement")}</Typography>
                      {group.options.map((option) => (
                        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} gap={2} key={option.productId}>
                          <Typography sx={{ flex: 1 }}>
                            {productById.get(Number(option.productId))?.name || t("fixedPriceMenu.productFallback", { id: option.productId })}
                          </Typography>
                          <TextField
                            size="small"
                            type="number"
                            label={t("fixedPriceMenu.extraPrice")}
                            inputProps={{ min: 0, step: 0.01 }}
                            value={option.extraPrice}
                            onChange={(event) => updateGroup(group.clientId, {
                              options: group.options.map((item) => (
                                item.productId === option.productId
                                  ? { ...item, extraPrice: event.target.value }
                                  : item
                              )),
                            })}
                            sx={{ width: { xs: "100%", sm: 140 } }}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>

          <Button
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
            onClick={() => setForm((current) => ({
              ...current,
              groups: [
                ...current.groups,
                newGroup(Math.max(0, ...current.groups.map((group) => Number(group.courseIndex) || 0)) + 1, t),
              ],
            }))}
          >
            {t("fixedPriceMenu.addCourse")}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>{t("fixedPriceMenu.cancel")}</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? t("fixedPriceMenu.saving") : t("fixedPriceMenu.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </SidebarLayout>
  );
}

export default FixedPriceMenusPage;
